import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useThemeColors, useStatusColor, getAlphaColor } from '../../theme/ColorUtils';
import authService, { User } from '../../services/authService';

// Define interface for webhook secret
interface WebhookSecret {
  id: number | string;
  name: string;
  created_at: string;
  expires_at?: string;
  last_used: string | null;
  secret?: string;
  masked_secret?: string;
}

interface NewWebhookSecret extends WebhookSecret {
  secret?: string;
  message?: string;
}

const WebhookSecretManager: React.FC = () => {
  const colors = useThemeColors();
  const [webhookSecrets, setWebhookSecrets] = useState<WebhookSecret[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [newWebhookSecret, setNewWebhookSecret] = useState<NewWebhookSecret | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [secretName, setSecretName] = useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [secretToDelete, setSecretToDelete] = useState<WebhookSecret | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');


  useEffect(() => {
    fetchWebhookSecrets();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    setUserLoading(true);
    try {
      const user = await authService.getUserProfile();
      setCurrentUser(user as User);
    } catch (err) {
      console.error('Error fetching current user:', err);
    } finally {
      setUserLoading(false);
    }
  };

  const fetchWebhookSecrets = async () => {
    setLoading(true);
    try {
      // Check if authenticated
      if (!authService.isAuthenticated()) {
        setError('Not authenticated. Please log in again.');
        setLoading(false);
        return;
      }

      // In production, fetch from actual API with auth headers
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/webhook-secrets`;
      if (process.env.NODE_ENV === 'development') {
        console.log('Fetching webhook secrets from:', apiUrl);
      }
      
      const response = await axios.get(apiUrl, {
        headers: authService.getAuthHeaders()
      });
      
      // Handle the response
      if (response.data && response.data.success) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Webhook secrets fetched successfully, count:', response.data.webhook_secrets?.length || 0);
        }
        
        if (response.data.webhook_secrets && Array.isArray(response.data.webhook_secrets)) {
          setWebhookSecrets(response.data.webhook_secrets);
        } else {
          setWebhookSecrets([]);
          setError('No webhook secrets found.');
        }
      } else {
        console.error('API response is not in expected format:', response.data);
        setWebhookSecrets([]);
        setError('Received unexpected data format from API. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching webhook secrets:', err);
      setWebhookSecrets([]); // Ensure webhookSecrets is set to an array even on error
      setError('Failed to fetch webhook secrets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhookSecret = async () => {
    try {
      // Check if authenticated
      if (!authService.isAuthenticated()) {
        showSnackbar('Not authenticated. Please log in again.', 'error');
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Creating webhook secret using backend');
      }
      
      // In production, call actual API with auth headers
      const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/webhook-secrets`;
      if (process.env.NODE_ENV === 'development') {
        console.log('Creating webhook secret at:', apiUrl);
        console.log('Authentication status:', authService.isAuthenticated());
        console.log('Secret name being sent:', secretName);
      }
      
      const response = await axios.post(
        apiUrl, 
        { name: secretName },
        { headers: authService.getAuthHeaders() }
      );
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Create webhook secret response received successfully');
        console.log('Response status:', response.status);
      }
      
      // Handle different response formats
      if (response.data && response.data.success && response.data.webhook_secret) {
        console.log('Using webhook_secret property from response');
        setNewWebhookSecret(response.data.webhook_secret);
        setWebhookSecrets([...webhookSecrets, response.data.webhook_secret]);
        showSnackbar('Webhook secret created successfully', 'success');
        // Reset dialog state
        setSecretName('');
      } else {
        // Unknown format, log error
        console.error('Unexpected response format from create webhook secret endpoint:', response.data);
        showSnackbar('Error creating webhook secret', 'error');
      }
      
      // Close dialog
      setOpenDialog(false);
    } catch (err) {
      console.error('Error creating webhook secret:', err);
      showSnackbar('Error creating webhook secret', 'error');
      // Keep dialog open on error to allow retry
    }
  };

  const handleDeleteWebhookSecret = async () => {
    if (!secretToDelete) return;
    
    try {
      // Check if authenticated
      if (!authService.isAuthenticated()) {
        showSnackbar('Not authenticated. Please log in again.', 'error');
        return;
      }

              if (process.env.NODE_ENV === 'development') {
          console.log('Deleting webhook secret with ID:', secretToDelete.id);
        }
        
        // In production, call actual API with auth headers
        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/lis-portal/webhook-secrets/${secretToDelete.id}`;
        if (process.env.NODE_ENV === 'development') {
          console.log('Deleting webhook secret at:', apiUrl);
        }
        
        const response = await axios.delete(
          apiUrl,
          { headers: authService.getAuthHeaders() }
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Delete webhook secret response received successfully');
        }
      
      if (response.data && response.data.success) {
        // Remove from state
        setWebhookSecrets(webhookSecrets.filter(secret => secret.id !== secretToDelete.id));
        showSnackbar('Webhook secret deleted successfully', 'success');
      } else {
        showSnackbar('Error deleting webhook secret', 'error');
      }
    } catch (err) {
      console.error('Error deleting webhook secret:', err);
      showSnackbar('Error deleting webhook secret', 'error');
    } finally {
      // Reset state
      setOpenDeleteDialog(false);
      setSecretToDelete(null);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showSnackbar('Copied to clipboard', 'success');
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
        showSnackbar('Failed to copy to clipboard', 'error');
      });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        if (text === currentUser?.id) {
          showSnackbar('Vendor ID copied to clipboard', 'success');
        } else {
          showSnackbar('Webhook secret copied to clipboard', 'success');
        }
      },
      () => {
        if (text === currentUser?.id) {
          showSnackbar('Failed to copy Vendor ID', 'error');
        } else {
          showSnackbar('Failed to copy webhook secret', 'error');
        }
      }
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (err) {
      return dateString;
    }
  };

  const getDaysUntilExpiry = (expiryDate: string | undefined) => {
    if (!expiryDate) return null;
    
    try {
      const expiry = new Date(expiryDate);
      const now = new Date();
      const diffTime = expiry.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (err) {
      return null;
    }
  };

  const getExpiryStatus = (expiryDate: string | undefined) => {
    const days = getDaysUntilExpiry(expiryDate);
    
    if (days === null) return { label: 'Unknown', color: 'default' };
    if (days <= 0) return { label: 'Expired', color: 'error' };
    if (days <= 30) return { label: `Expires in ${days} days`, color: 'warning' };
    return { label: `Expires in ${days} days`, color: 'success' };
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2" color={colors.text.primary}>
          Webhook Secrets
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create Webhook Secret
        </Button>
      </Box>
      
      {/* Info message */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Webhook secrets are used to authenticate webhook API calls by signing the request payload. Use these secrets when implementing webhook authentication using the X-LIS-Signature header.
      </Alert>

      {/* Vendor ID Display */}
      {currentUser && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: getAlphaColor(colors.primary.main, 0.1), border: `1px solid ${colors.primary.main}` }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: colors.primary.main }}>
            <KeyIcon sx={{ mr: 1 }} />
            Vendor Credentials
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 2, fontWeight: 'medium' }}>
              Vendor ID:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: 'background.paper', 
              p: 1, 
              borderRadius: 1,
              border: `1px solid ${colors.border}`
            }}>
              <Typography variant="body1" sx={{ mr: 1, fontFamily: 'monospace' }}>
                {currentUser.id}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(currentUser.id)}
                title="Copy Vendor ID"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            This is your unique vendor identifier from Cognito. It's required for API authentication and webhook configuration.
          </Typography>
        </Paper>
      )}
      
      {/* Loading and error states */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Display new webhook secret */}
      {newWebhookSecret && (
        <Card sx={{ mb: 3, bgcolor: getAlphaColor(colors.success , 0.1) }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your New Webhook Secret
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This is the only time your webhook secret will be displayed in full. Please copy it now and store it securely.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', flexGrow: 1, p: 1, bgcolor: colors.background.alt }}>
                {newWebhookSecret.secret}
              </Typography>
              <IconButton 
                color="primary" 
                onClick={() => handleCopyToClipboard(newWebhookSecret.secret || '')}
                aria-label="Copy to clipboard"
              >
                <CopyIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => setNewWebhookSecret(null)}
              >
                Done
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Webhook secrets table */}
      {!loading && webhookSecrets.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Secret</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhookSecrets.map((secret) => {
                const expiryStatus = getExpiryStatus(secret.expires_at);
                
                return (
                  <TableRow key={secret.id}>
                    <TableCell>{secret.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {secret.masked_secret || '••••••••••••••••'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(secret.created_at)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={expiryStatus.label}
                        color={expiryStatus.color as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {secret.last_used ? formatDate(secret.last_used) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSecretToDelete(secret);
                          setOpenDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* No webhook secrets message */}
      {!loading && webhookSecrets.length === 0 && !error && (
        <Alert severity="info">
          You don't have any webhook secrets yet. Create one to get started.
        </Alert>
      )}
      
      {/* Create webhook secret dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Create Webhook Secret</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Provide a name for your webhook secret. The secret will be generated automatically.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Webhook Secret Name"
            type="text"
            fullWidth
            value={secretName}
            onChange={(e) => setSecretName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateWebhookSecret} 
            color="primary"
            disabled={!secretName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete webhook secret confirmation dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Webhook Secret</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the webhook secret "{secretToDelete?.name}"? 
            This action cannot be undone, and any applications using this webhook secret will no longer be able to authenticate.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteWebhookSecret} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WebhookSecretManager; 